import {
  ChangeDetectionStrategy,
  Component,
  effect,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SourceField } from '@sciflow/cite';
import { updateDocumentState } from 'editor';
import { selectDocumentStateIsDirty } from 'projects/editor/src/lib/editor.reducer';
import { ProjectService } from 'projects/reference-app/src/editor/shared/services/project.service';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { DcaStore, FileService } from 'shared';
import { ErrorDialogService } from '../error-dialog/error-dialog.service';
import { Reference } from '../reference.model';
import { RenderingService } from '../rendering.service';
import { ReferenceManagementService } from '../services/reference-management.service';
import {
  addReferences,
  clearReferenceIdToEdit,
  setActiveReferenceId,
  setReferenceIdToEdit,
  setReferenceSortOrder,
} from '../store/references.actions';
import {
  selectActiveReferenceId,
  selectAreAllReferencesCited,
  selectCitationlist,
  selectReferenceIdToEdit,
  selectReferenceList,
  selectReferenceSortOrder,
} from '../store/references.reducer';
import {
  buildReferenceAppearanceIndexByCitationOrder,
  sortReferences,
} from './reference-sort.utils';
import { getReferenceSourceClass } from './reference-source.utils';

type ReferenceInTextCitation = {
  source: string;
  id: string;
  textContent: string;
};

type ReferenceListItem = Reference & {
  citationsfromEditor?: ReferenceInTextCitation[];
};

@Component({
  selector: 'lib-references',
  templateUrl: './references.component.html',
  styleUrls: ['./references.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReferencesComponent implements OnDestroy {
  getReferenceSourceClass = getReferenceSourceClass;

  displayedColumns: string[] = ['bib', 'isStarred'];
  stop$ = new Subject<void>();
  loading = true;
  projectId: string;

  /** HTML element we use for as a drag image */
  dragElement;

  /** Selected reference for editing */
  activeReference = signal<any | null>(null);
  selectedReferenceId = signal<string | null>(null);

  /** Current sort order for the reference list */
  sortOrder = toSignal(this.store.select(selectReferenceSortOrder), {
    initialValue: 'none' as const,
  });

  /** True once every CSL-backed reference has at least one in-text citation */
  allCslReferencesCited = toSignal(this.store.select(selectAreAllReferencesCited), {
    initialValue: false,
  });

  private readonly mappedReferenceList$ = this.store.select(selectReferenceList).pipe(
    switchMap((referenceList) =>
      this.store
        .select(selectCitationlist)
        .pipe(map((nodeCitations) => ({ nodeCitations, referenceList }))),
    ),
    map(({ nodeCitations, referenceList }) => {
      const citations: any[] = [];
      if (nodeCitations.length !== 0) {
        nodeCitations.forEach((element) => {
          element.citationItems.map((ref) =>
            citations.push({
              source: ref.id,
              id: element.citationID,
              textContent: element.plainCitation,
            }),
          );
        });
      }

      if (referenceList.length === 0) {
        this.loading = false;
        return [] as any[];
      }

      const mapped = referenceList.map((reference: ReferenceListItem) => {
        const referencesInText = [] as any;
        reference = { ...reference };
        citations.map((citation) => {
          if (citation.source === reference.csl?.id) {
            reference.citationsfromEditor = [];
            referencesInText.push(citation);
            reference.citationsfromEditor = referencesInText;
          }
        });
        return reference;
      });

      return mapped as ReferenceListItem[];
    }),
  );

  referenceList$ = combineLatest([
    this.mappedReferenceList$,
    this.store.select(selectCitationlist),
    this.store.select(selectReferenceSortOrder),
  ]).pipe(
    map(([refs, citations, sortOrder]) => {
      const appearanceIndexById = buildReferenceAppearanceIndexByCitationOrder(citations || []);
      return sortReferences(refs, sortOrder, appearanceIndexById);
    }),
  );

  isMaximized$ = this.dcaStore.isDcaExpanded;

  @ViewChild('fileUpload', { static: true }) fileUpload;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private renderingService: RenderingService,
    private fileService: FileService,
    private router: Router,
    private projectService: ProjectService,
    private errorDialogService: ErrorDialogService,
    private dcaStore: DcaStore,
    private referenceManagementService: ReferenceManagementService,
  ) {
    effect(() => {
      const isExpanded = this.dcaStore.isDcaExpanded();
      if (!isExpanded) {
        this.activeReference.set(null);
      }
    });

    effect(() => {
      this.sortOrder();
      firstValueFrom(this.referenceList$).then((sortedRefs) => {
        const orderedIds = sortedRefs.map((ref: any) => ref.csl?.id || ref.key).filter(Boolean);
        this.referenceManagementService.sortBibliographyNodes(orderedIds);
      });
    });

    this.store
      .select(selectActiveReferenceId)
      .pipe(takeUntil(this.stop$))
      .subscribe((referenceId) => {
        const nextId = typeof referenceId === 'string' ? referenceId : null;
        this.selectedReferenceId.set(nextId);
        if (!nextId || !this.dcaStore.isDcaShown()) {
          return;
        }
        this.scrollToReference(nextId);
      });

    combineLatest([
      this.store.select(selectReferenceIdToEdit),
      this.store.select(selectReferenceList),
    ])
      .pipe(takeUntil(this.stop$))
      .subscribe(([referenceId, references]) => {
        if (!referenceId) {
          return;
        }
        const match = references.find(
          (reference) => this.getReferenceId(reference) === referenceId,
        );
        if (!match) {
          return;
        }
        this.activeReference.set(match);
        this.store.dispatch(clearReferenceIdToEdit());
      });
  }

  async uploadReferenceFile(): Promise<void> {
    this.fileUpload.nativeElement.click();
  }

  setSortOrder(sortOrder: 'none' | 'author'): void {
    this.store.dispatch(setReferenceSortOrder({ sortOrder }));
  }

  public async onUpload(event) {
    event.preventDefault();
    event.stopPropagation();
    const file = (event.srcElement || event.target).files.item(0);
    this.fileUpload.nativeElement.value = '';

    if (!file.name.endsWith('.bib')) {
      this.errorDialogService.openDialog('Only .bib files are currently supported.');
      return;
    }

    const result = await this.fileService.uploadFile(this.projectId, file);
    if (result) {
      const references = await this.projectService.getReferences(result.projectId);
      this.store.dispatch(addReferences({ references }));
    }
  }

  editCitationInText(citation, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.activeReference.set(null);
    this.store.dispatch(setActiveReferenceId({ referenceId: null }));
    this.dcaStore.collaspeDcaExpansion();

    const element = document.getElementById(citation.id);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
    element?.focus();
    this.router.navigate(['citation', citation.id], { relativeTo: this.activatedRoute });
  }

  editReference(reference, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    const referenceId = this.getReferenceId(reference);
    this.store.dispatch(setActiveReferenceId({ referenceId }));
    if (referenceId) {
      this.store.dispatch(setReferenceIdToEdit({ referenceId }));
    }
  }

  toggleMaximize(): void {
    this.dcaStore.toggleDcaExpansion();
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  onDragStart(event, data): void {
    const isMaximized = this.dcaStore.isDcaMaximized();
    if (isMaximized) {
      event.preventDefault();
    } else {
      this.dragStart(event, data);
    }
  }

  async dragStart(event, data): Promise<void> {
    const referenceId = data?.csl?.id;
    if (!referenceId) {
      this.errorDialogService.openDialog('No citation data found');
      return;
    }

    const citation =
      this.renderingService.render([{ id: referenceId }], [data.csl]) ||
      String(data?.csl?.rawCitation || data?.bib || data?.csl?.title || '').trim();
    const source = { citationItems: [{ id: referenceId }], properties: { noteIndex: 0 } };
    const cite = `<cite data-source="${encodeURI(SourceField.toString(source))}">${citation}</cite>`;

    this.dragElement = document.createElement('cite');
    this.dragElement.innerHTML = `<b>${citation}</b>`;
    document.body.appendChild(this.dragElement);
    event.dataTransfer.setDragImage(this.dragElement, 0, 25);

    event.dataTransfer.setData('text/html', cite);
  }

  dragEnd(event, row): void {
    if (this.dragElement) {
      document.body.removeChild(this.dragElement);
    }
  }

  selectReference(reference: any): void {
    this.activeReference.set(reference);
    this.store.dispatch(setActiveReferenceId({ referenceId: this.getReferenceId(reference) }));
  }

  isReferenceSelected(reference: ReferenceListItem): boolean {
    const active = this.activeReference();
    if (active?.key && reference.key && active.key === reference.key) {
      return true;
    }
    if (active?.csl?.id && reference.csl?.id && active.csl.id === reference.csl.id) {
      return true;
    }
    const selectedId = this.selectedReferenceId();
    if (!selectedId) {
      return false;
    }
    return reference.key === selectedId || reference.csl?.id === selectedId;
  }

  private getReferenceId(reference: any): string | null {
    return reference?.key || reference?.csl?.id || null;
  }

  private scrollToReference(referenceId: string): void {
    requestAnimationFrame(() => {
      const element = document.querySelector(
        `[data-reference-id="${referenceId}"]`,
      ) as HTMLElement | null;
      element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
  }

  async updateReference(updatedReference: Reference): Promise<void> {
    try {
      await this.referenceManagementService.updateReference(updatedReference);

      const isDirty = await firstValueFrom(this.store.select(selectDocumentStateIsDirty));

      if (!isDirty) {
        this.store.dispatch(updateDocumentState({ dirty: true }));
      }
    } catch (error) {
      console.error('Error updating bibliography:', error);
      this.errorDialogService.openDialog('Failed to update bibliography formatting');
    }
  }
}
